import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { DiscAlbumIcon, HeartIcon, HomeIcon, LibraryIcon, ListMusicIcon, Music2Icon, PlusIcon, SearchIcon } from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
export const navItems = [{
  to: '/',
  label: 'Home',
  Icon: HomeIcon
}, {
  to: '/search',
  label: 'Search',
  Icon: SearchIcon
}, {
  to: '/now-playing',
  label: 'Now Playing',
  Icon: Music2Icon
}, {
  to: '/favorites',
  label: 'Favourites',
  Icon: HeartIcon
}, {
  to: '/library',
  label: 'Your Library',
  Icon: LibraryIcon
}];
export function Sidebar() {
  const {
    favorites,
    playlists,
    createPlaylist
  } = useLibrary();
  const [composing, setComposing] = useState(false);
  const [name, setName] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createPlaylist(name);
    setName('');
    setComposing(false);
  };
  return <aside className="hidden w-60 flex-none flex-col border-r border-ink-line bg-ink-panel/80 px-5 py-6 backdrop-blur md:flex" data-id="element-139" data-mp="1lz5i1vjhkmo4">
      <div className="flex items-center gap-2.5" data-id="element-140" data-mp="1qr5idp3gxhl8">
        <DiscAlbumIcon className="h-6 w-6 text-gold" aria-hidden="true" data-id="element-141" data-mp="1cz8ww3wb25xt" />
        <span className="font-display text-xl tracking-tight text-cream" data-id="element-142" data-mp="1cww754kb2ra7">Noctuzan</span>
      </div>
      <p className="mt-2 text-xs italic text-muted" data-id="element-143" data-mp="1gyhr19v9c5tw">late-night radio, on demand</p>

      <nav aria-label="Main" className="mt-9" data-id="element-144" data-mp="24qy8bi767ts5">
        <ul className="space-y-1" data-id="element-145" data-mp="29ozu4avze77b">
          {navItems.map(({
          to,
          label,
          Icon
        }) => <li key={to} data-id="element-146" data-mp="1vpgsm0xodu3z">
              <NavLink to={to} end={to === '/'} className={({
            isActive
          }) => `flex items-center gap-3 rounded-sm px-2.5 py-2 text-sm transition-colors duration-150 ease-night focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft ${isActive ? 'bg-ink-raised text-gold' : 'text-muted hover:text-cream'}`} data-id="element-147" data-mp="1vq9schy7vjcm">
                <Icon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-148" data-mp="2r0eq0ybyzv46" />
                {label}
                {to === '/favorites' && favorites.length > 0 && <span className="ml-auto text-[11px] tabular-nums text-muted" data-id="element-149" data-mp="2h77nrmcj0v8b">{favorites.length}</span>}
              </NavLink>
            </li>)}
        </ul>
      </nav>

      <div className="mt-8 flex min-h-0 flex-1 flex-col" data-id="element-150" data-mp="289nlqb6uauwn">
        <div className="flex items-center justify-between" data-id="element-151" data-mp="238gdkb0sbtpg">
          <h2 className="text-xs uppercase tracking-wide text-muted" data-id="element-152" data-mp="23b2jk681215k">Playlists</h2>
          <button type="button" onClick={() => setComposing(c => !c)} aria-label="New playlist" aria-expanded={composing} className="rounded-full p-1 text-muted transition-colors duration-150 ease-night hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-153" data-mp="1yaesxlnpnmbv">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-154" data-mp="1afcuz5slpzb8" />
          </button>
        </div>

        <AnimatePresence initial={false} data-id="element-155" data-mp="1fd4jdpu1f3ao">
          {composing && <motion.form onSubmit={submit} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="overflow-hidden" data-id="element-156" data-mp="1fbbh0mqgj58h">
              <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="Name this set…" aria-label="New playlist name" className="mt-2 w-full rounded border border-ink-line bg-ink-base px-2 py-1.5 text-xs text-cream placeholder:text-muted focus:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-157" data-mp="we0hr24tsbt1" />
            </motion.form>}
        </AnimatePresence>

        <ul className="mt-3 min-h-0 flex-1 space-y-0.5 overflow-y-auto scroll-thin pr-1" data-id="element-158" data-mp="2fdzwydduf0b6">
          {playlists.length === 0 && !composing && <li className="text-xs leading-relaxed text-muted" data-id="element-159" data-mp="2odwkucserv56">
              Build a set from any track&apos;s <span className="text-cream" data-id="element-160" data-mp="2kct29iiz7l09">＋</span> menu.
            </li>}
          {playlists.map(playlist => <li key={playlist.id} data-id="element-161" data-mp="2ybao1apbmnfj">
              <NavLink to={`/playlist/${playlist.id}`} className={({
            isActive
          }) => `flex items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm transition-colors duration-150 ease-night focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft ${isActive ? 'bg-ink-raised text-gold' : 'text-muted hover:text-cream'}`} data-id="element-162" data-mp="2ya9a8ygx7mu4">
                <ListMusicIcon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-163" data-mp="3c4iev05kvew6" />
                <span className="truncate" data-id="element-164" data-mp="3vpsfr4qtihsl">{playlist.name}</span>
                <span className="ml-auto flex-none text-[11px] tabular-nums" data-id="element-165" data-mp="3vnfxeqh5e5f0">{playlist.tracks.length}</span>
              </NavLink>
            </li>)}
        </ul>
      </div>

      <div className="mt-5 flex-none border-t border-ink-line pt-5 text-xs leading-relaxed text-muted" data-id="element-166" data-mp="3moay3hxri81s">
        <p data-id="element-167" data-mp="3qomjwy1v2eeu">
          <span className="text-cream" data-id="element-168" data-mp="3qsir3rb96a5t">{favorites.length}</span> saved {favorites.length === 1 ? 'track' : 'tracks'}
        </p>
        <p className="mt-2" data-id="element-169" data-mp="3hphmxw5y6t4s">Previews run ~30 seconds, courtesy of the iTunes Search API.</p>
      </div>
    </aside>;
}